import React from "react";
import { APP_VERSION } from "@/version";

async function hardReset() {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (e) {
    /* ignore */
  }
  window.location.reload();
}

const AUTO_RETRY_SECONDS = 4;

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, countdown: AUTO_RETRY_SECONDS };
    this._timer = null;
    this._tick = null;
  }

  static getDerivedStateFromError() {
    return { hasError: true, countdown: AUTO_RETRY_SECONDS };
  }

  componentDidCatch(error, info) {
    const msg = String(error?.message || error || "");
    this.setState({ message: msg });
    try {
      const key = "clazing_err_count";
      const rec = JSON.parse(sessionStorage.getItem(key) || "{\"n\":0,\"t\":0}");
      const now = Date.now();
      const n = now - rec.t < 90000 ? rec.n + 1 : 1;
      sessionStorage.setItem(key, JSON.stringify({ n, t: now }));
      this._repeat = n;
    } catch (e) { this._repeat = 1; }
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      let userLabel = "";
      try { const u = JSON.parse(localStorage.getItem("clazing_user") || "null"); userLabel = u ? `${u.name || ""} <${u.email || ""}> ${u.role || ""}` : ""; } catch (e) { /* ignore */ }
      fetch(`${base}/api/client-error`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: msg, stack: String((error && error.stack) || "") + "\n--component--\n" + String((info && info.componentStack) || ""), url: window.location.href, userAgent: navigator.userAgent, version: APP_VERSION, user: userLabel }) }).catch(() => {});
    } catch (e) { /* ignore */ }
    if (/ChunkLoadError|Loading chunk|dynamically imported module|Failed to fetch|Unexpected token '<'/i.test(msg) || this._repeat >= 3) {
      // Kapotte/verouderde bundel of blijvende fout: volledige reset (cache + service worker) i.p.v. eindeloos blauw scherm.
      hardReset();
      return;
    }
    // Zachte fout: probeer automatisch te herstellen (terug naar de app) na een paar seconden.
    this._tick = setInterval(() => {
      this.setState((s) => ({ countdown: Math.max(0, s.countdown - 1) }));
    }, 1000);
    this._timer = setTimeout(() => this.softRetry(), AUTO_RETRY_SECONDS * 1000);
  }

  clearTimers() {
    if (this._timer) { clearTimeout(this._timer); this._timer = null; }
    if (this._tick) { clearInterval(this._tick); this._tick = null; }
  }

  softRetry = () => {
    this.clearTimers();
    // Reset de foutstatus zodat de app opnieuw rendert met de (verse) cloud-data.
    this.setState({ hasError: false, countdown: AUTO_RETRY_SECONDS });
  };

  componentWillUnmount() {
    this.clearTimers();
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="error-boundary"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "18px",
          padding: "24px",
          textAlign: "center",
          background: "#1C64B0",
          color: "#fff",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <div style={{ fontSize: "48px" }}>⚠️</div>
        <h1 style={{ fontSize: "22px", fontWeight: 700, margin: 0 }}>Er ging even iets mis</h1>
        <p style={{ maxWidth: 360, opacity: 0.9, lineHeight: 1.5, margin: 0 }}>
          De app keert automatisch terug naar het invoerscherm
          {this.state.countdown > 0 ? ` over ${this.state.countdown} seconde${this.state.countdown === 1 ? "" : "n"}…` : "…"}
        </p>
        {this.state.message && (
          <div data-testid="error-detail" style={{ maxWidth: 420, width: "100%", background: "rgba(0,0,0,0.25)", borderRadius: 10, padding: "10px 12px", fontSize: 12, textAlign: "left", wordBreak: "break-word", fontFamily: "monospace" }}>
            <div style={{ opacity: 0.7, marginBottom: 4 }}>Foutmelding (automatisch doorgestuurd naar beheer · {APP_VERSION})</div>
            {this.state.message}
          </div>
        )}
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "center" }}>
          <button
            data-testid="error-retry-btn"
            onClick={this.softRetry}
            style={{
              padding: "12px 28px",
              fontSize: "16px",
              fontWeight: 600,
              borderRadius: "9999px",
              border: "none",
              background: "#fff",
              color: "#1C64B0",
              cursor: "pointer",
            }}
          >
            Nu terug naar de app
          </button>
          <button
            data-testid="error-reload-btn"
            onClick={hardReset}
            style={{
              padding: "12px 28px",
              fontSize: "16px",
              fontWeight: 600,
              borderRadius: "9999px",
              border: "1px solid rgba(255,255,255,0.6)",
              background: "transparent",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            App volledig herladen
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
